import {
  CALL_SCRIPTS_PROMPT_MAX_CHARS,
  CALL_SCRIPT_REQUIREMENTS,
  CALL_SCRIPT_STEP_LIBRARY,
  callScriptStepAllowsRequirements,
  findCallScriptLibraryStep
} from "@swivl-mono/types";
import type { CallScriptLibraryStep, CallScriptPromptEntry, CallScriptRequirement, CallScriptSnapshot, CallScriptStep } from "@swivl-mono/types";

/** The library every renderer reads a step's sentence from — the platform catalog unless the caller resolved the org's (ENG-3402). */
export type StepLibrary = ReadonlyArray<CallScriptLibraryStep>;

/**
 * Deterministic rendering of call scripts (ENG-3356) — no LLM, no locale.
 * The same function feeds the bot's prompt section and Studio's preview so
 * what the operator sees is what the model reads.
 */

/** "their name and a phone number" / "their name, a phone number and an email address" */
export function joinRequirementPhrases(requirements: CallScriptRequirement[]): string {
  const phrases = requirements.map((r) => CALL_SCRIPT_REQUIREMENTS[r]?.phrase).filter((p): p is string => !!p);
  if (phrases.length === 0) return "";
  if (phrases.length === 1) return phrases[0];
  return `${phrases.slice(0, -1).join(", ")} and ${phrases[phrases.length - 1]}`;
}

/** The sentence a step renders to, requirements clause included. "" for a slug the library does not have. */
export function renderCallScriptStep(step: CallScriptStep, library: StepLibrary = CALL_SCRIPT_STEP_LIBRARY): string {
  const sentence = findCallScriptLibraryStep(step.slug, library)?.sentence?.trim() ?? "";
  if (!sentence) return "";
  const list = joinRequirementPhrases(step.requirements ?? []);
  const clause = list ? ` If you do not yet have ${list}, ask for ${step.requirements.length === 1 ? "it" : "them"} before going on.` : "";
  return `${sentence}${clause}`;
}

/** One script → its prompt block. "" when it has no renderable steps. */
export function renderCallScript(script: CallScriptSnapshot, library: StepLibrary = CALL_SCRIPT_STEP_LIBRARY): string {
  const lines: string[] = [];
  let n = 0;
  for (const step of script.steps ?? []) {
    const text = renderCallScriptStep(step, library);
    if (!text) continue;
    lines.push(`${++n}. ${text}`);
  }
  if (lines.length === 0) return "";
  const when = script.whenToUse.trim();
  return [`### ${script.name.trim()}`, when ? `Use this script when: ${when}` : "", ...lines].filter(Boolean).join("\n");
}

export interface RenderedCallScripts {
  text: string;
  entries: CallScriptPromptEntry[];
  /** Slugs that did not fit the budget and were left out, in order. */
  dropped: string[];
}

/**
 * All scripts in force → the section body, within `maxChars`. Scripts are
 * kept whole: when the next one does not fit, it and everything after it are
 * dropped (and reported) rather than cut mid-sentence.
 */
/** The line rendered above a sequence day's starting script (ENG-3403). */
export function renderStartingScriptLine(sequenceName: string): string {
  return `This conversation started from the ${sequenceName} sequence. Begin with this script, and switch to another only if the customer takes the conversation there.`;
}

export function renderCallScriptsForPrompt(
  scripts: Array<CallScriptSnapshot & { slug: string; startingScript?: boolean }>,
  maxChars: number = CALL_SCRIPTS_PROMPT_MAX_CHARS,
  /** Name of the sequence the conversation came from — rendered above the starting script. */
  startingSequenceName?: string,
  library: StepLibrary = CALL_SCRIPT_STEP_LIBRARY
): RenderedCallScripts {
  const entries: CallScriptPromptEntry[] = [];
  const dropped: string[] = [];
  let total = 0;
  for (const script of scripts) {
    const body = renderCallScript(script, library);
    if (!body) continue;
    const text = script.startingScript && startingSequenceName ? `${renderStartingScriptLine(startingSequenceName)}\n${body}` : body;
    const cost = text.length + (entries.length > 0 ? 2 : 0);
    if (total + cost > maxChars || dropped.length > 0) {
      dropped.push(script.slug);
      continue;
    }
    entries.push({ slug: script.slug, name: script.name, text });
    total += cost;
  }
  return { text: entries.map((e) => e.text).join("\n\n"), entries, dropped };
}

/** Structural validation shared by the server (authoritative) and Studio (early feedback). Empty array = valid. No cap on the number of steps (Chuck, 2026-09-28) — the prompt budget is the only bound. */
export function validateCallScriptSteps(steps: CallScriptStep[], library: StepLibrary = CALL_SCRIPT_STEP_LIBRARY): string[] {
  const errors: string[] = [];
  if (!Array.isArray(steps)) return ["steps must be a list"];
  const seen = new Set<string>();
  steps.forEach((step, i) => {
    const at = `step ${i + 1}`;
    if (!step || typeof step.slug !== "string" || !step.slug) {
      errors.push(`${at}: has no step slug`);
      return;
    }
    const known = findCallScriptLibraryStep(step.slug, library);
    if (!known) errors.push(`${at}: unknown step "${step.slug}"`);
    if (seen.has(step.slug)) errors.push(`${at}: "${step.slug}" appears twice`);
    seen.add(step.slug);
    for (const r of step.requirements ?? []) {
      if (!CALL_SCRIPT_REQUIREMENTS[r]) errors.push(`${at}: unknown requirement "${r}"`);
    }
    if ((step.requirements ?? []).length > 0 && known && !callScriptStepAllowsRequirements(step, library)) {
      errors.push(`${at}: this step does not collect details, so it cannot have must-haves`);
    }
  });
  return errors;
}
